import React, { forwardRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { Row, Select, Col, Form, Flex, Input, Button, Dropdown, MenuProps } from "antd";
import { useState } from "react";
import { WidgetNode } from "./TextEditor/components/Widget";
import { theme } from "./TextEditor/constants";
import { initialConfig } from "../components/TextEditor/constants";
import "./../style.css";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { LexicalEditor, $getSelection, $getRoot, $createTextNode, $isRangeSelection, $isTextNode, $isElementNode } from "lexical";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { CustomSpanNode } from "./TextEditor/constants";
import { useTranslation } from "react-i18next";
import { BeautifulMentionsMenuProps, BeautifulMentionsPlugin, BeautifulMentionsTheme, BeautifulMentionNode, $createBeautifulMentionNode } from "lexical-beautiful-mentions";
import { PlusOutlined, SmileOutlined } from "@ant-design/icons";

interface IEditEmailContent {
  addWidget: Function;
  editorRef: React.MutableRefObject<LexicalEditor | null | undefined>;
  orgStructureWidgets?: object[]
  definedWidgets?: object[]
  widgetItems?: MenuProps['items'],
  setCurrentSelected: Function
  subjectRef?: React.MutableRefObject<LexicalEditor | null | undefined>;
  subjectValue?: string;
  onSubjectChange?: Function;
  subjectError?: string;
}

const contentEditableSubject: React.CSSProperties = {
  width: "100%",
  minHeight: "32px",
  height: "32px",
  boxSizing: "border-box",
  resize: "none",
  fontSize: "14px",
  caretColor: "#444",
  position: "relative",
  tabSize: 1,
  outline: 0,
  color: "#000",
  margin: "0px",
  padding: "4px 11px",
  lineHeight: "1.5715",
};

const containerSubject: React.CSSProperties = {
  width: "100%",
  minHeight: "32px",
  height: "32px",
  backgroundColor: "white",
  padding: "0px",
  borderRadius: "6px",
  boxSizing: "border-box",
  border: "1px solid #d9d9d9",
  overflowY: "hidden",
  overflowX: "hidden",
  transition: "all 0.2s",
};

const beautifulMentionsTheme: BeautifulMentionsTheme = {
  "@": "highlighted",
  "@Focused": "outline-none shadow-md ...",
  "due:": {
    trigger: "text-blue-400 ...",
    value: "text-orange-400 ...",
  },
}

const CustomMenuItem = forwardRef<
  HTMLLIElement,
  BeautifulMentionsMenuProps
>(({ selected, item, ...props }, ref) => (
  <li
    className={`m-0 flex ... ${selected ? "bg-gray-100 ..." : "bg-white ..."}`}
    {...props}
    style={{
      listStyleType: "none",
      whiteSpace: "nowrap"
    }}
  />
));
const SubjectEditor = ({
  subjectRef,
  orgStructureWidgets,
  definedWidgets,
  widgetItems,
  onFocus
}: {
  subjectRef: React.MutableRefObject<LexicalEditor | null | undefined>;
  orgStructureWidgets?: object[];
  definedWidgets?: object[];
  widgetItems?: MenuProps['items'];
  onFocus?: () => void;
}) => {
  const [isSubjectFocused, setIsSubjectFocused] = useState(false);
  const { t } = useTranslation();

  let list: string[] = []

  // First add from widgetItems if available
  if (widgetItems && widgetItems.length > 0) {
    widgetItems.forEach((item: any) => {
      if (item.label) {
        list.push(item.label)
      }
    })
  } else {
    // Fallback to orgStructureWidgets and definedWidgets
    if (orgStructureWidgets && orgStructureWidgets.length > 0) {
      orgStructureWidgets.forEach((r: any) => list.push(r.label))
    }
    if (definedWidgets && definedWidgets.length > 0) {
      definedWidgets.forEach((r: any) => list.push(r.label))
    }
  }

  const mentionItems = {
    "@": list,
  };

  const containerStyle = {
    ...containerSubject,
    borderColor: isSubjectFocused ? "#1890ff" : "#d9d9d9",
    boxShadow: isSubjectFocused ? "0 0 0 2px rgba(24, 144, 255, 0.2)" : "none",
  };

  const handleFocus = () => {
    setIsSubjectFocused(true);
    if (onFocus) onFocus();
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Only blur if the focus is not moving to a related element (like dropdown)
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsSubjectFocused(false);
    }
  };

  return (
      <div 
      className="subject-editor-container"
      style={containerStyle}
      onClick={() => {
        // Explicitly maintain focus on subject when clicked
        if (subjectRef?.current) {
          subjectRef.current.focus();
          handleFocus();
        }
      }}
    >
      <RichTextPlugin
        contentEditable={
          <ContentEditable 
            style={contentEditableSubject} 
            className="reset"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        }
        placeholder={<div style={{fontSize: '14px', color: '#bfbfbf', pointerEvents: 'none', padding: '4px 11px', lineHeight: '1.5715'}}>{t("Enter subject")}</div>}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <EditorRefPlugin editorRef={subjectRef} />
      <HistoryPlugin />
      {list.length > 0 && (
        <BeautifulMentionsPlugin
          items={mentionItems}
          menuComponent={() => null}
        />
      )}
      <TabIndentationPlugin />
    </div>
  );
};

const EditEmailContent: React.FC<IEditEmailContent> = ({
  addWidget,
  editorRef,
  orgStructureWidgets,
  definedWidgets,
  widgetItems,
  setCurrentSelected,
  subjectRef,
  subjectValue,
  onSubjectChange,
  subjectError
}) => {
  const [selectedWidget, setSelectedWidget] = useState<any>(subjectValue || "");
  const { t } = useTranslation();

  const onChangeWidget = (value: string) => {
    addWidget(value);
    setSelectedWidget(value);
    if (onSubjectChange) {
      onSubjectChange(value);
    }
  };
  const handleSubjectFocus = () => {
    setCurrentSelected('SUBJECT')
  }
  const addWidgetToSubject = (widget: any) => {
    if (!subjectRef?.current) return;
    setCurrentSelected('SUBJECT');
    subjectRef.current.focus();
    setTimeout(() => {
      subjectRef.current!.update(() => {
        let selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          const lastDescendant = $getRoot().getLastDescendant();
          if (lastDescendant) {
            lastDescendant.selectEnd();
            selection = $getSelection();
          }
        }
        if (!$isRangeSelection(selection)) return;

        const anchorNode = selection.anchor.getNode();
        const mentionNode = $createBeautifulMentionNode("@", widget.label, { id: widget.id });
        const spaceNode = $createTextNode(" ");

        if ($isTextNode(anchorNode)) {
          const offset = selection.anchor.offset;
          if (offset === anchorNode.getTextContentSize()) {
            anchorNode.insertAfter(mentionNode);
          } else {
            const [, afterNode] = anchorNode.splitText(offset);
            if (afterNode) {
              afterNode.insertBefore(mentionNode);
            } else {
              anchorNode.insertAfter(mentionNode);
            }
          }
          mentionNode.insertAfter(spaceNode);
        } else if ($isElementNode(anchorNode)) {
          anchorNode.append(mentionNode, spaceNode);
        }
        spaceNode.select();
      });
    }, 0);
  };

  // Update selectedWidget when subjectValue prop changes
  React.useEffect(() => {
    if (subjectValue !== undefined) {
      setSelectedWidget(subjectValue);
    }
  }, [subjectValue]);
  let allDefinedWidgets: any = []
  if (orgStructureWidgets && orgStructureWidgets.length) {
    orgStructureWidgets.forEach((r: any, index: number) => { 
      allDefinedWidgets.push({ label: r.label, value: r.label, key: "orgStructure" + index }) 
    })
  }
  if (definedWidgets && definedWidgets.length) {
    definedWidgets.forEach((r: any, index: number) => { 
      allDefinedWidgets.push({ label: r.label, value: r.label, key: "definedWidget" + index }) 
    })
  }

  return (
    <>      <Row gutter={8}>
        <Col span={24}>
          <Form.Item
            label={t("Subject")}
            required
            validateStatus={subjectError ? "error" : undefined}
            help={subjectError}
          >
            <Flex gap={10}>
              {subjectRef ? (
                <LexicalComposer
                  initialConfig={{
                    ...initialConfig,
                    editorState: null,
                  }}
                >
                  <SubjectEditor
                    subjectRef={subjectRef}
                    orgStructureWidgets={orgStructureWidgets || []}
                    definedWidgets={definedWidgets || []}
                    widgetItems={widgetItems}
                    onFocus={handleSubjectFocus}
/>
                </LexicalComposer>
              ) : (
                <Input
                  onChange={(e) => onChangeWidget(e.target.value)}
                  onFocus={handleSubjectFocus}
                  placeholder={t("Enter subject")}
                  value={selectedWidget}
                />
              )}
              <Dropdown
                menu={{
                  items: widgetItems?.map((item: any) => ({
                    key: item.id || item.key,
                    // Already resolved for the selected template language — do
                    // not re-translate into the app locale.
                    label: item.label,
                    onClick: () => {
                      // Use dedicated function to add widget only to subject
                      addWidgetToSubject(item);
                    }
                  })) || [],
                }}
                trigger={['click']}
              >
                <Button ghost style={{ color: "#141BCB", borderColor: "#141BCB" }}>
                  <span style={{ paddingRight: '3px' }}>
                    <PlusOutlined />
                  </span>
                  {t("Form Field")}
                </Button>
              </Dropdown>
            </Flex>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default EditEmailContent;
